import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import fp from "lodash/fp";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import OutsideClickHandler from "react-outside-click-handler";

/* modules */
import { getStagesProjects } from "../../modules/user";

/* hooks */
import { useAuthenticate } from "./widgets/hooks";

/* widgets */
import { AccessibleProjects, RecentAssetSharedMe, ZendeskNotice, RecentComments } from "./widgets";
import RecentContents from "./widgets/recent-contents";

import { siteMode } from "../../cores/siteMode";
import { initDashboardWidget, clearDashboardWidget } from "../../modules/dashboard";
import Loader from "@components/loader";
import compareVersions, { VERSION_CM_8558_METADATA_TEMPLATE } from "@cores/version";

import {
    IconChevronDown,
    IconChevronUp,
    IconSetting,
    IconRefresh,
    IconProjects,
    IconVideoType,
    IconPeopleType,
    IconAudioType,
    IconPhotoType,
    IconAssetType,
    IconNotice,
    IconReply,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";

/* App에서 지원하는 위젯들 */
const supportWidgets = [
    //{ name: "ZendeskNotice", title: `SM Entertainment 공지사항`, icon: `sprite-notice-sm`, component: ZendeskNotice },
    {
        name: "AccessibleProjects",
        title: `Accessible Projects`,
        color: `#11193f`,
        icon: <IconProjects color={"inherit"} size={12} />,
        component: AccessibleProjects,
    },
    {
        name: "latestVideos",
        title: `Recent Videos`,
        color: `#405fff`,
        icon: <IconVideoType color={"inherit"} size={12} />,
        component: RecentContents,
        contentType: "videos",
        query: {
            columns: [
                `contentId`,
                `contentName`,
                `contentDuration`,
                `contentThumbnailUrl`,
                `contentStatus`,
                `contentCreatedAt`,
                `contentOwnerId`,
                `contentOwnerName`,
                `contentOwnerUsername`,
                `contentType`,
            ],
            filters: [
                { key: `contentType`, value: `VIDEO` },
                { key: `projectId`, value: "{{:currentProjectId:}}" },
                { key: "contentOwnerId", value: "{{:loginUserId:}}" },
            ],
        },
    },
    {
        name: "latestPeople",
        title: `Recent People`,
        color: `#5f62e5`,
        icon: <IconPeopleType color={"inherit"} size={16} />,
        component: RecentContents,
        contentType: "people",
        query: {
            filters: [
                { key: `contentType`, value: `PEOPLE` },
                { key: `projectId`, value: "{{:currentProjectId:}}" },
                { key: "contentOwnerId", value: "{{:loginUserId:}}" },
            ],
        },
    },
    {
        name: "latestMusic",
        title: `Recent Music`,
        color: `#e86894`,
        icon: <IconAudioType color={"inherit"} size={12} />,
        component: RecentContents,
        contentType: "music",
        query: {
            filters: [
                { key: `contentType`, value: `MUSIC` },
                { key: `projectId`, value: "{{:currentProjectId:}}" },
                { key: "contentOwnerId", value: "{{:loginUserId:}}" },
            ],
        },
    },
    {
        name: "latestPhotos",
        title: `Recent Photos`,
        color: `#5d8fff`,
        icon: <IconPhotoType color={"inherit"} size={12} />,
        component: RecentContents,
        contentType: "photos",
        query: {
            filters: [
                { key: `contentType`, value: `PHOTO` },
                { key: `projectId`, value: "{{:currentProjectId:}}" },
                { key: "contentOwnerId", value: "{{:loginUserId:}}" },
            ],
        },
    },
    {
        name: "RecentAssetSharedMe",
        title: `Recent Assets Shared to Me`,
        color: `#7047f3`,
        icon: <IconAssetType color={"inherit"} size={12} />,
        component: RecentAssetSharedMe,
    },

    // {name: 'widget5', title: `TITLE5`, icon: `sprite-transcoding-sm`, component: Widget5},
];

const externalSupportWidgets = [
    {
        type: "MY_COMMENTS",
        color: theme.palette.greySecondary.main,
        icon: <IconReply size={12} htmlColor={theme.palette.common.white} />,
        component: RecentComments,
    },
    {
        type: "ZENDESK_TOPICS",
        color: theme.palette.greySecondary.main,
        icon: <IconNotice size={10} />,
        component: ZendeskNotice,
    },
    {
        type: "ZENDESK_SECTIONS",
        color: theme.palette.greySecondary.main,
        icon: <IconNotice size={10} />,
        component: ZendeskNotice,
    },
];

const Dashboard = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // const { user } = useAuthenticate(); //User Data로 project내에 있는 user 정보 조회
    const stateSelector = createSelector(
        (state) => state.stage,
        (state) => state.project,
        (state) => state.dashboard,
        (stage, project, dashboard) => {
            let widgets = fp.getOr(
                [
                    //"ZendeskNotice",
                    "AccessibleProjects",
                    "latestVideos",
                    "latestPeople",
                    "latestMusic",
                    "latestPhotos",
                    "RecentAssetSharedMe",
                    "RecentComments",
                ],
                `widgets`,
                stage,
            ); //스테이지에서 제공하는 위젯들
            if (fp.isEqual(siteMode.get(), "CMS"))
                widgets = widgets.filter((widget) => widget.name !== RecentAssetSharedMe);
            return {
                stage,
                project,
                widgets,
                dashboard: dashboard.dashboardWidgetList,
                user: stage.user,
            };
        },
    );
    const state = useSelector(stateSelector);

    const stage = state.stage;
    const user = state.user;
    const hideContents = stage && stage.extensions && stage.extensions["HIDE_LIVE"] === "true";

    const [widgets, setWidgets] = useState([]);
    const [externalWidgets, setExternalWidgets] = useState([]);
    const [allExternalWidgets, setAllExternalWidgets] = useState(null); //v1, v2 이런식으로 명칭 구분되도록
    const [isDropdownMenu, setIsDropdownMenu] = useState(false);

    const handleToggleWidget = useCallback(
        (widget, checked) => {
            let result;
            if (checked) {
                result = [widget].concat(widgets);
            } else {
                const findWidgetIndex = fp.findIndex((item) => fp.isEqual(item, widget), widgets);
                result = fp.pullAt(findWidgetIndex, widgets);
            }

            let stateWidgets = JSON.parse(localStorage.getItem(`widgets`)) || [];
            let foundWidgets = stateWidgets.find((widgetInfo) => widgetInfo.projectId === state.project.id) ?? {
                projectId: state.project.id,
                stageId: state.stage.id,
                widgets: [],
            };
            foundWidgets.widgets = result.map((widg) => widg.name);

            stateWidgets = [
                ...stateWidgets.filter(
                    (widgetInfo) => widgetInfo.stageId !== state.stage.id || widgetInfo.projectId !== state.project.id,
                ),
                foundWidgets,
            ];

            localStorage.setItem("widgets", JSON.stringify(stateWidgets));
            setWidgets(result);
        },
        [widgets],
    );

    const handleToggleExternalWidget = useCallback(
        (widget, checked) => {
            let toggledWidgets = [];
            if (checked) {
                toggledWidgets = [...externalWidgets, widget];
            } else {
                const findWidgetIndex = externalWidgets.findIndex((widg) => widg.id === widget.id);
                const result = fp.pullAt(findWidgetIndex, externalWidgets);
                toggledWidgets = result;
            }
            handleClickToggleWidget(toggledWidgets);
        },
        [externalWidgets, handleClickToggleWidget],
    );

    const handleDeleteWidget = useCallback(
        (widgetName) => {
            const composer = fp.pipe(
                fp.filter((widget) => !fp.isEqual(fp.get(`name`, widget), widgetName)),
                setWidgets,
            );
            composer(widgets);
            // console.log("---------delete widget");
        },
        [widgets],
    );

    const setMoveWidgets = useCallback(
        (index, offset) => {
            const cloneWidgets = fp.cloneDeep(widgets);
            const moveIndex = index + offset;
            const newIndex = moveIndex >= cloneWidgets.length ? cloneWidgets.length - 1 : moveIndex;

            if (newIndex >= cloneWidgets.length) {
                fp.set(``, cloneWidgets.length - 1, newIndex);
            }
            setIsDropdownMenu(false);
            cloneWidgets.splice(newIndex, 0, cloneWidgets.splice(index, 1)[0]);
            setWidgets(cloneWidgets);
            // console.log('---------move widget');
        },
        [widgets],
    );

    const handleMoveWidget = useCallback(
        (sourceId, id) => {
            const sourceIndex = fp.findIndex((widget) => fp.isEqual(fp.get(`name`, widget), sourceId), widgets);
            const destinationIndex = fp.findIndex((widget) => fp.isEqual(fp.get(`name`, widget), id), widgets);
            const offset = destinationIndex - sourceIndex;

            if (fp.isEqual(-1, sourceIndex) || fp.isEqual(-1, destinationIndex)) return false;

            setMoveWidgets(sourceIndex, offset);
        },
        [widgets, setMoveWidgets],
    );

    const handleRefresh = useCallback(() => {
        dispatch(getStagesProjects());
        //NOTE: 한번에 refresh 될 수 있는 방법을 찾아야함..
    }, []);

    const handleClickToggleWidget = useCallback(
        (exWidgets) => {
            let stateWidgets = JSON.parse(localStorage.getItem(`externalWidgets`)) || [];
            let foundWidgets = stateWidgets.find((widgetInfo) => widgetInfo.projectId === state.project.id) ?? {
                projectId: state.project.id,
                stageId: state.stage.id,
                widgets: [],
            };
            foundWidgets.widgets = foundWidgets.widgets.map((widg) => {
                if (exWidgets?.find((exWidg) => exWidg.id === widg.id)) {
                    return { ...widg, isExposed: true };
                } else {
                    return { ...widg, isExposed: false };
                }
            });
            localStorage.setItem("externalWidgets", JSON.stringify(stateWidgets));
            setExternalWidgets(exWidgets);
        },
        [state.dashboard, state.stage, state.project],
    );

    useEffect(() => {
        //init
        const parsedWidgets = JSON.parse(localStorage.getItem(`widgets`)) ?? [];
        const projectWidgets = parsedWidgets.find((widgetInfo) => widgetInfo.projectId === state.project.id);
        const stateWidgets = projectWidgets?.widgets || state.widgets;

        if (!projectWidgets) {
            localStorage.setItem(
                "widgets",
                JSON.stringify(
                    parsedWidgets.concat([
                        {
                            stageId: state.stage.id,
                            projectId: state.project.id,
                            widgets: stateWidgets,
                        },
                    ]),
                ),
            );
        }

        const composer = fp.pipe(
            fp.reduce((acc, name) => {
                fp.map((widget) => {
                    fp.isEqual(fp.get(`name`, widget), name) &&
                        !(
                            fp.isEqual(siteMode.get(), "CMS") &&
                            fp.isEqual(fp.get(`name`, widget), "RecentAssetSharedMe")
                        ) &&
                        acc.push(widget);
                }, supportWidgets_);
                return acc;
            }, []),
            setWidgets,
        );
        composer(stateWidgets);
    }, [supportWidgets_, state.stage.id, state.project.id]);

    useEffect(() => {
        if (
            compareVersions(stage.version, VERSION_CM_8558_METADATA_TEMPLATE) < 0 ||
            !state.dashboard.data?.length ||
            state.dashboard.pending
        )
            return;

        const externalWidgetsSetting = JSON.parse(localStorage.getItem("externalWidgets")) || [];
        const filteredExternalWidgets = externalWidgetsSetting.filter(
            (widgetInfo) => widgetInfo.spaceId !== state.stage.id || widgetInfo.projectId !== state.project.id,
        );
        const foundExternalWidgets = externalWidgetsSetting.find(
            (widgetInfo) => widgetInfo.spaceId === state.stage.id && widgetInfo.projectId === state.project.id,
        );
        const stateWidgets = state.dashboard.data?.sort((a, b) => b.fullWidth - a.fullWidth);

        const parsedWidgets = {
            spaceId: state.stage.id,
            projectId: state.project.id,
            widgets: stateWidgets.map((widg) => {
                const widgetParams = externalSupportWidgets.find((el) => widg.type.indexOf(el.type) >= 0);
                if (["ZENDESK_SECTIONS", "ZENDESK_TOPICS"].includes(widgetParams?.type)) {
                    return {
                        ...widgetParams,
                        id: widg.id,
                        name: widg.name,
                        title: widg.name,
                        params: widg.params,
                        logo: widg.logoUrl,
                        viewAllUrl: widg.viewAllUrl,
                        isExposeEditable: false,
                        //isExposeEditable: widg.isExposeEditable, //TODO: 차후 설정에 대시보드 관련 설정이 생길 경우 수정이 필요합니다.
                        isExposed:
                            (!widg.isExposeEditable && widg.isExposed) ||
                            (widg.isExposeEditable &&
                                (foundExternalWidgets?.widgets?.find((x) => x.id === widg.id)?.isExposed ??
                                    widg.isExposed)),
                        fullWidth: false,
                    };
                } else {
                    return {
                        ...widgetParams,
                        id: widg.id,
                        name: widg.name,
                        title: widg.name,
                        params: widg.params,
                        logo: widg.logoUrl,
                        viewAllUrl: widg.viewAllUrl,
                        isExposeEditable: widg.isExposeEditable,
                        isExposed: widg.isExposed,
                        fullWidth: false,
                    };
                }
            }),
        };

        setAllExternalWidgets(parsedWidgets.widgets);
        //parsedWidgets 전체 mapping->isExposeEditable false일 경우 externalWidgetsSetting 값 상관없이 parsedWidgets로 고정
        //isExposedEditable이 true일 경우 1. localStorage에 저장되어있는 값이면 해당 값을 사용. 2. 없을 경우 isExposed 사용
        //localStorage에 저장하는 것은 이번에 불러온 위젯 목록에 한함(이전에 저장되었던 현재는 없는 위젯 정보는 날림)
        const showExternalWidgets = parsedWidgets.widgets.filter(
            (widg) => widg.isExposed && ["ZENDESK_SECTIONS", "ZENDESK_TOPICS", "MY_COMMENTS"].includes(widg.type),
        );
        setExternalWidgets(showExternalWidgets);

        localStorage.setItem("externalWidgets", JSON.stringify([...filteredExternalWidgets, parsedWidgets]));
    }, [state.dashboard]);

    useEffect(() => {
        if (compareVersions(stage.version, VERSION_CM_8558_METADATA_TEMPLATE) >= 0) {
            dispatch(initDashboardWidget());
        }

        return () => {
            // clear
            setIsDropdownMenu(false);
            dispatch(clearDashboardWidget());
            setExternalWidgets([]);
            setAllExternalWidgets([]);
        };
    }, [state.stage, state.project]);

    const [supportWidgets_, setSupportWidgets_] = useState(
        supportWidgets.filter((widget) => {
            if (hideContents) {
                return ["RecentAssetSharedMe", "AccessibleProjects"].some((n) => n === widget.name);
            }
            if (!(fp.isEqual(siteMode.get(), "CMS") && fp.isEqual(fp.get(`name`, widget), "RecentAssetSharedMe")))
                return true;
            return false;
        }),
    );

    useEffect(() => {
        const sw = supportWidgets.filter((widget) => {
            if (hideContents) {
                return ["RecentAssetSharedMe", "AccessibleProjects"].some((n) => n === widget.name);
            }
            if (!(fp.isEqual(siteMode.get(), "CMS") && fp.isEqual(fp.get(`name`, widget), "RecentAssetSharedMe")))
                return true;
            return false;
        });
        setSupportWidgets_(sw);
    }, [siteMode.get()]);

    return (
        <div className={`dashboard`}>
            <div className={`dashboard-head `}>
                <div className="dashboard-head-tools">
                    <OutsideClickHandler
                        onOutsideClick={() => {
                            setIsDropdownMenu(false);
                        }}
                    >
                        <div className={`btn-dropdown btn-dropdown-toolbar ${isDropdownMenu ? "dropdown-open" : ""}`}>
                            <button
                                type="button"
                                className="btn btn-transparent"
                                onClick={() => {
                                    setIsDropdownMenu(!isDropdownMenu);
                                }}
                            >
                                <IconSetting size={16} color={`${isDropdownMenu ? "primary" : ""}`} />
                                <strong className={`${isDropdownMenu ? "text-primary" : ""}`}>
                                    {t("common::label::Widget Settings", "Widget Settings")}
                                </strong>
                                {isDropdownMenu ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}
                            </button>
                            <div className="btn-dropdown-layer">
                                <div>
                                    <span>{t(`common::label::Selected`, "Selected")} </span>
                                    <strong className="text-primary">{fp.get(`length`, widgets)} </strong>
                                    <strong>of {fp.get("length", supportWidgets_)}</strong>
                                </div>
                                <ul className="widget-item-list">
                                    {compareVersions(stage.version, VERSION_CM_8558_METADATA_TEMPLATE) > 0 &&
                                        allExternalWidgets?.map((widget, i) => (
                                            <li
                                                key={fp.get(`name`, widget)}
                                                className={`item ${
                                                    externalWidgets.find((widg) => widg.id === widget.id)
                                                        ? "selected"
                                                        : ""
                                                } `}
                                            >
                                                <button
                                                    type={`button`}
                                                    // onClick={() => handleAddWidget(widget)}
                                                    // disabled
                                                >
                                                    <label htmlFor={`exwidget${i}`} className="form-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            id={`exwidget${i}`}
                                                            checked={
                                                                externalWidgets.find(
                                                                    (widg) => widg.id === widget.id,
                                                                ) !== undefined
                                                            }
                                                            onChange={(e) =>
                                                                handleToggleExternalWidget(widget, e.target.checked)
                                                            }
                                                            disabled={
                                                                (fp.isEqual(1, widgets.length) &&
                                                                    fp.isEqual(widget, fp.head(widgets))) ||
                                                                !widget.isExposeEditable
                                                            }
                                                        />
                                                        <i />
                                                        <span
                                                            style={{
                                                                backgroundColor: `${fp.getOr(
                                                                    "#11193f",
                                                                    `color`,
                                                                    widget,
                                                                )}`,
                                                                color: `#fff`,
                                                            }}
                                                        >
                                                            {fp.get(`icon`, widget)}
                                                        </span>
                                                        <strong>{fp.get(`title`, widget)}</strong>
                                                    </label>
                                                </button>
                                            </li>
                                        ))}
                                    {supportWidgets_.map((widget, i) => (
                                        <li
                                            key={fp.get(`name`, widget)}
                                            className={`item ${
                                                !fp.pipe(
                                                    fp.filter((item) =>
                                                        fp.isEqual(fp.get(`name`, widget), fp.get(`name`, item)),
                                                    ),
                                                    fp.isEmpty,
                                                )(widgets)
                                                    ? "selected"
                                                    : ""
                                            } `}
                                        >
                                            <button
                                                type={`button`}
                                                // onClick={() => handleAddWidget(widget)}
                                                // disabled
                                            >
                                                <label htmlFor={`widget${i}`} className="form-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        id={`widget${i}`}
                                                        checked={
                                                            !fp.pipe(
                                                                fp.filter((item) =>
                                                                    fp.isEqual(
                                                                        fp.get(`name`, widget),
                                                                        fp.get(`name`, item),
                                                                    ),
                                                                ),
                                                                fp.isEmpty,
                                                            )(widgets)
                                                        }
                                                        onChange={(e) => {
                                                            widget.dataType === "api"
                                                                ? handleToggleExternalWidget(widget, e.target.checked)
                                                                : handleToggleWidget(widget, e.target.checked);
                                                        }}
                                                        disabled={
                                                            fp.isEqual(1, widgets.length) &&
                                                            fp.isEqual(widget, fp.head(widgets))
                                                        }
                                                    />
                                                    <i />
                                                    <span
                                                        style={{
                                                            backgroundColor: `${fp.getOr("#11193f", `color`, widget)}`,
                                                            color: `#fff`,
                                                        }}
                                                    >
                                                        {fp.get(`icon`, widget)}
                                                    </span>
                                                    <strong>{fp.get(`title`, widget)}</strong>
                                                </label>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                {/* TODO: 다음 스프린트때 작업 */}
                                {/*<div className="btns">*/}
                                {/*    <button type="button" className="btn btn-outline-default btn-sm">*/}
                                {/*        <span>{t(`common::label::${"Cancel"}`)}</span>*/}
                                {/*    </button>*/}
                                {/*    <button type="button" className="btn btn-outline-primary btn-sm">*/}
                                {/*        <span>{t(`common::label::${"Apply"}`)}</span>*/}
                                {/*    </button>*/}
                                {/*</div>*/}
                            </div>
                        </div>
                    </OutsideClickHandler>
                    <hr />
                    <button type={`button`} onClick={handleRefresh} className="btn btn-icon-solid">
                        <IconRefresh size={16} />
                    </button>
                </div>
            </div>
            <div className={`dashboard-body`}>
                <div className="widgets flex-column">
                    {state.dashboard.pending ? (
                        <Loader />
                    ) : (
                        <DndProvider backend={HTML5Backend}>
                            <div className={"d-flex flex-wrap"}>
                                {fp.map((widget) => {
                                    const Widget = fp.get(`component`, widget);
                                    return (
                                        <Widget
                                            key={fp.get(`name`, widget)}
                                            user={user}
                                            name={fp.get(`name`, widget)}
                                            id={widget.id}
                                            contentType={fp.get(`contentType`, widget)}
                                            title={
                                                widgets.find((x) => x.title === widget.title)
                                                    ? t(`common::label::${fp.get(`title`, widget)}`)
                                                    : fp.get(`title`, widget)
                                            }
                                            fullWidth={fp.get(`fullWidth`, widget)}
                                            icon={fp.get(`icon`, widget)}
                                            color={fp.get(`color`, widget)}
                                            removable={!fp.isEqual(1, widgets.length)}
                                            query={fp.get("query", widget)}
                                            logo={widget.logo}
                                            params={widget.params}
                                            viewAllUrl={widget.viewAllUrl}
                                            onMove={handleMoveWidget}
                                            onDelete={handleDeleteWidget} //trigger event
                                            type={"notice"}
                                        />
                                    );
                                }, externalWidgets)}
                            </div>
                            <div className={"d-flex flex-wrap"}>
                                {fp.map((widget) => {
                                    const Widget = fp.get(`component`, widget);
                                    return (
                                        <Widget
                                            key={fp.get(`name`, widget)}
                                            user={user}
                                            name={fp.get(`name`, widget)}
                                            id={widget.id}
                                            contentType={fp.get(`contentType`, widget)}
                                            title={
                                                widgets.find((x) => x.title === widget.title)
                                                    ? t(`common::label::${fp.get(`title`, widget)}`)
                                                    : fp.get(`title`, widget)
                                            }
                                            fullWidth={fp.get(`fullWidth`, widget)}
                                            icon={fp.get(`icon`, widget)}
                                            color={fp.get(`color`, widget)}
                                            removable={!fp.isEqual(1, widgets.length)}
                                            query={fp.get("query", widget)}
                                            logo={widget.logo}
                                            params={widget.params}
                                            viewAllUrl={widget.viewAllUrl}
                                            onMove={handleMoveWidget}
                                            onDelete={handleDeleteWidget} //trigger event
                                        />
                                    );
                                }, widgets)}
                            </div>
                        </DndProvider>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
